import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { cn } from '@/utils/formatter';

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700",
        secondary: "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700",
        tertiary: "bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600",
        destructive: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700",
        outline: "border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 focus:ring-gray-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800",
        ghost: "bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-300 dark:hover:bg-gray-800",
        link: "bg-transparent text-blue-600 underline-offset-4 hover:underline focus:ring-blue-500 dark:text-blue-400",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    isLoading = false, 
    loadingText, 
    children, 
    disabled,
    type = "button", // Define type="button" como padrão para evitar submit de formulários
    ...props 
  }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={cn(
          buttonVariants({ variant, size }),
          "inline-grid place-items-center [grid-template-areas:'stack']",
          className
        )}
        {...props}
      >
        <span
          className={cn(
            isLoading && "invisible",
            "flex items-center gap-2 [grid-area:stack]"
          )}
        >
          {children}
        </span>
        <AiOutlineLoading3Quarters
          aria-label={loadingText || "Carregando"}
          className={cn(
            isLoading ? "visible" : "invisible",
            "size-5 animate-spin transition-opacity [grid-area:stack]"
          )}
        />
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
export { SimpleButton } from './SimpleButton';
export default Button;
